import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useToast } from '@chakra-ui/react';

import type { UpdateMailingListsTargetQuantity } from '@enterprise/api';
import { ensureError, isDefined, isNotDefined } from '@enterprise/core';
import { useWindowOpen } from '@enterprise/hooks';
import { useLoadingStore } from '@enterprise/ui';

import type { NthCalculatorProps } from '@components/nth-calculator/types/nth-calculator.props';

import { useNthCalculatorStore } from '@components/nth-calculator/stores/nth-calculator-store';

import type { UseNthCalculatorConfigurationResult } from './types/nth-calculator-configuration.type';

import { useGetTwentileCountsByMailing } from './get-twentile-count-by-mailing.hook';
import { useUpdateMailingListsTargetQuantity } from './update-mailing-lists-target-quantity.hook';

export const useNthCalculatorConfiguration = ({
  mailingId,
  onNewWindow,
}: Pick<NthCalculatorProps, 'mailingId' | 'onNewWindow'>): UseNthCalculatorConfigurationResult => {
  const [isOverThreshold, setIsOverThreshold] = useState(false);

  const { setIsLoading } = useLoadingStore();
  const toast = useToast();

  const clearDraft = useNthCalculatorStore(({ clearDraft }) => {
    return clearDraft;
  });

  const { mutate: updateMailingListsTargetQuantity } = useUpdateMailingListsTargetQuantity({
    onError: (error) => {
      setIsLoading(false);

      toast({
        description: ensureError(error).message,
        duration: 4000,
        isClosable: true,
        status: 'error',
        title: 'Failed to update target quantities',
      });
    },
    onSuccess: () => {
      setIsLoading(false);

      if (isDefined(mailingId)) {
        clearDraft(mailingId);
      }
      toast({
        description: 'Target quantities updated successfully.',
        duration: 3000,
        isClosable: true,
        status: 'success',
        title: 'Updated',
      });
    },
  });

  const { data: rows } = useGetTwentileCountsByMailing(mailingId);

  const dropBottom4Ref = useRef<(() => void) | null>(null);
  const resetRef = useRef<(() => void) | null>(null);
  const getMailingListUpdatesRef = useRef<(() => UpdateMailingListsTargetQuantity[]) | null>(null);

  const packageTarget = useMemo(() => {
    return rows?.[0]?.targetQuantity ?? 0;
  }, [rows]);

  const totalFirstOutput = useMemo(() => {
    return (
      rows?.reduce((sum, row) => {
        return sum + row.firstOutput;
      }, 0) ?? 0
    );
  }, [rows]);

  const [open, { isOpen }] = useWindowOpen({
    features: {
      height: 800,
      location: 'no',
      menubar: 'no',
      resizable: 'yes',
      scrollbars: 'yes',
      status: 'no',
      toolbar: 'no',
      width: 1200,
    },
    target: '_blank',
  });
  const handleNewWindow = () => {
    open(isNotDefined(mailingId) ? '/datastream/nth-calculator' : `/datastream/nth-calculator?mailingId=${mailingId}`);
  };

  const handleOverThresholdChange = useCallback((isOver: boolean) => {
    setIsOverThreshold(isOver);
  }, []);

  const handleUpdate = useCallback(() => {
    if (isNotDefined(mailingId)) {
      return;
    }

    const lists = getMailingListUpdatesRef.current?.() ?? [];

    setIsLoading(true);

    updateMailingListsTargetQuantity({ lists, mailingId });
  }, [mailingId, updateMailingListsTargetQuantity, setIsLoading]);

  useEffect(() => {
    if (isOpen) {
      onNewWindow?.();
    }
  }, [isOpen, onNewWindow]);

  return {
    dropBottom4Ref,
    getMailingListUpdatesRef,
    handleNewWindow,
    handleOverThresholdChange,
    handleUpdate,
    isOverThreshold,
    packageTarget,
    resetRef,
    totalFirstOutput,
  };
};
